import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Dropdown, WebSocketComponent, usePermanentRealtimeDealers, useSelectedLocation } from "components";
import { usePermanentLocations } from "components/DealersAndLocationsDropdown/hooks";
import { useWebSocketStatus } from "components/WebSocket/hooks";
import { useRegisterNavAction } from "hooks";
import { DMSIDS } from "models";
import { QueryEditor, QueryHistoryTable, QueryResult } from "modules/DMSv3/CustomQueries/components";
import { useCustomQueryHistoryQuery } from "modules/DMSv3/CustomQueries/components/QueryHistoryTable/hooks";
import "modules/DMSv3/CustomQueries/CustomQueriesDashboard.scss";
import { setCustomQueriesFilters, setSelectedQuery, useCustomQueriesFilters, useSelectedQuery, useSubmitCustomQueryMutation } from "modules/DMSv3/CustomQueries/hooks";
import { getDmsName } from "utils/dms";

import type { DMSCustomQueryHistory } from "models";

export const CustomQueries = () => {
  const { t } = useTranslation();

  const { dealers } = usePermanentRealtimeDealers();
  const { locations } = usePermanentLocations();
  const { selectedLocation } = useSelectedLocation();
  const { isConnected } = useWebSocketStatus();
  const { data: filters } = useCustomQueriesFilters();
  const { data: selectedQuery } = useSelectedQuery();
  const { history, isFetchingHistory, refetchHistory } = useCustomQueryHistoryQuery();
  const { mutateAsync: submitQuery, isPending: isSubmittingQuery } = useSubmitCustomQueryMutation();

  const [name, setName] = useState("");
  const [statement, setStatement] = useState("");

  const locationOptions = dealers.flatMap(
    dealer =>
      dealer.locations
        ?.filter(location => location.dms_id > DMSIDS.UNKNOWN)
        .map(location => ({ key: location.id, value: location.id, text: `${location.name} - ${getDmsName(location.dms_id)} - ${dealer.name}` })) ?? []
  );

  const pageLocation = locations.find(location => location.id === filters.dealer_location_id);

  useEffect(() => {
    if (filters.dealer_location_id || !locationOptions.length) return;

    const dealerLocationId = locationOptions.find(option => option.value === selectedLocation?.id)?.value ?? locationOptions[0].value;
    setCustomQueriesFilters({ ...filters, dealer_location_id: dealerLocationId });
  }, [locationOptions, selectedLocation?.id, filters.dealer_location_id]);

  useEffect(() => {
    if (!isConnected || !pageLocation?.notifier_key) return;

    WebSocketComponent.subscribeToLocationQueue(pageLocation.notifier_key);
    return () => WebSocketComponent.unsubscribeFromLocationQueue(pageLocation.notifier_key);
  }, [isConnected, pageLocation?.notifier_key]);

  useRegisterNavAction(() => refetchHistory());

  const handleLocationChange = (dealerLocationId?: number) => {
    if (!dealerLocationId) return;
    setCustomQueriesFilters({ dealer_location_id: dealerLocationId, page: 1 });
    setSelectedQuery(null);
  };

  const handleNameChange = (value: string) => {
    setName(value);
    setSelectedQuery(null);
  };

  const handleStatementChange = (value: string) => {
    setStatement(value);
    setSelectedQuery(null);
  };

  const handleRunQuery = async () => {
    if (!filters.dealer_location_id || !name || !statement) return;
    await submitQuery({ dealer_location_id: filters.dealer_location_id, name, query: statement });
  };

  const handleSelectRow = (query: DMSCustomQueryHistory) => {
    setSelectedQuery(query);
    setName(query.name);
    setStatement(query.statement);
  };
  const handlePageChange = (page: number) => setCustomQueriesFilters({ ...filters, page });

  const isRunDisabled = !filters.dealer_location_id || !name || !statement;

  return (
    <div className="CustomQueriesDashboard">
      <div className="header-row">
        <Dropdown
          fluid
          selection
          search
          selectOnBlur={false}
          placeholder={t("v8_select_location", "Select a location")}
          options={locationOptions}
          value={filters.dealer_location_id ?? undefined}
          onChange={handleLocationChange}
        />
      </div>

      <div className="content-grid">
        <div className="editor-column">
          <QueryEditor
            name={name}
            statement={statement}
            isRunDisabled={isRunDisabled}
            isLoading={isSubmittingQuery}
            onNameChange={handleNameChange}
            onStatementChange={handleStatementChange}
            onRunQuery={handleRunQuery}
          />
        </div>

        <div className="history-column">
          <div className="history-title">{t("v8_query_history", "Query history")}</div>
          <QueryHistoryTable
            items={history?.items}
            page={filters.page}
            nbPages={history?.nb_pages}
            nbItems={history?.nb_items}
            isLoading={isFetchingHistory}
            selectedQueryId={selectedQuery?.id}
            onSelectRow={handleSelectRow}
            onPageChange={handlePageChange}
          />
        </div>

        <div className="result-column">
          <QueryResult query={selectedQuery} isLoading={isSubmittingQuery} />
        </div>
      </div>
    </div>
  );
};
